import { Button, Grid, Input, Modal, Typography } from "@mui/material";
import { useUploadAsset } from "../../hooks/useAsset";
import { Asset, ASSET_TYPE, AssetRequestMetadata } from "../../types/types";
import { AssetCard } from "./../AssetCard";
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/FolderZip';
import { BACKEND_URL, TOTAL_GROUP_ASSET_SIZE_MB } from "../../constants";
import { useMemo } from "react";

export interface AssetsModalProps {
  open: boolean;
  assets?: Asset[];

  onUpload: (assetId: string, metadata: AssetRequestMetadata) => void;
  onClose: () => void;
  onDelete: (asset: Asset) => void;
}

export const AssetsModal = (props: AssetsModalProps) => {
  const { uploadAsset, loading, error } = useUploadAsset();

  const assetTotalSize = useMemo(() => {
    let size = 0;
    props.assets?.filter(a => [ASSET_TYPE.MESH_DATA, ASSET_TYPE.TEXTURE_DATA].includes(a.type)).forEach(a => {
      size += a.size;
    });

    return (size / (1024 * 1024)).toFixed(2);
  }, [props.assets]);

  const handleTextureFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg"];

    if (!allowedTypes.includes(file.type)) {
      alert("Only PNG and JPG files are allowed!");
      return;
    }

    const metadata = { name: file.name, type: ASSET_TYPE.TEXTURE_DATA };
    const result = await uploadAsset(file, metadata);

    if (result.success) {
      console.log("Uploaded successfully:", result.data);
      await props.onUpload(result.data?.assetId ?? '', metadata);
    } else {
      console.error("Upload error:", result.message);
    }
  };

  const handleMeshFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".obj")) {
      alert("Only .obj files are allowed!");
      return;
    }

    const metadata = { name: file.name, type: ASSET_TYPE.MESH_DATA };
    const result = await uploadAsset(file, metadata);

    if (result.success) {
      await props.onUpload(result.data?.assetId ?? '', metadata);
    } else {
      // TODO - display error
      console.error("Upload error:", result.message);
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Grid
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          padding: 3,
          minWidth: 400,
          boxShadow: 24,
          outline: 'none',
        })}
        spacing={4}
        container
      >
        <Grid size={12}>
          <Typography color="textPrimary" variant="h6">Assets Management</Typography>
        </Grid>
        <Grid size={12}>
          <Typography color="textSecondary">These are all of the assets that belong to your group and can be used in your scenes.
            <br />You currently are using <b>{assetTotalSize}MB</b> out of <b>{TOTAL_GROUP_ASSET_SIZE_MB}MB</b>.<br />Just a heads up that as you add more, the initial load of your scene will take longer.</Typography>
        </Grid>
        <Grid size={12} container spacing={1}>
          {props.assets && props.assets.map(a => (
            <Grid key={a.id} size={3}>
              <AssetCard
                asset={a}
                onDelete={async () => {
                  await props.onDelete(a);
                }}
                onDownload={async (asset: Asset) => {
                  try {
                    // Call your backend endpoint
                    const token = localStorage.getItem("jwt");
                    const response = await fetch(`${BACKEND_URL}/assets/download/${asset.id}`, {
                      method: "GET",
                      headers: {
                        Authorization: token ?? "",
                      },
                    });

                    if (!response.ok) {
                      throw new Error("Failed to download asset");
                    }

                    // Convert to a blob
                    const blob = await response.blob();

                    // Create a temporary object URL
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary <a> to trigger download
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${asset.name}.zip`;
                    document.body.appendChild(a);
                    a.click();

                    // Cleanup
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error("Error downloading asset:", error);
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
        <Grid size={12}>
          <label htmlFor="texture-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<UploadIcon />}
            >
              Upload Texture (PNG/JPG)
            </Button>
          </label>
          <Input
            id="texture-upload"
            type="file"
            style={{ display: "none" }} // hide actual input
            inputProps={{
              accept: "image/png, image/jpeg",
            }}
            onChange={(e) =>
              handleTextureFileUpload(e as React.ChangeEvent<HTMLInputElement>)
            }
          />
        </Grid>
        <Grid size={12}>
          <label htmlFor="mesh-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<UploadIcon />}
            >
              Upload Mesh (OBJ)
            </Button>
          </label>
          <Input
            id="mesh-upload"
            type="file"
            style={{ display: "none" }} // hide actual input
            inputProps={{
              accept: ".obj",
            }}
            onChange={(e) =>
              handleMeshFileUpload(e as React.ChangeEvent<HTMLInputElement>)
            }
          />
        </Grid>
        <Grid size={12}>
          <label htmlFor="group-download">
            <Button
              variant="contained"
              component="span"
              startIcon={<DownloadIcon />}
              onClick={async () => {
                try {
                  // Call your backend endpoint
                  const token = localStorage.getItem("jwt");
                  const response = await fetch(`${BACKEND_URL}/assets/download`, {
                    method: "GET",
                    headers: {
                      Authorization: token ?? "",
                    },
                  });

                  if (!response.ok) {
                    throw new Error("Failed to download group assets");
                  }

                  // Convert to a blob
                  const blob = await response.blob();

                  // Create a temporary object URL
                  const url = window.URL.createObjectURL(blob);

                  // Create a temporary <a> to trigger download
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "group_assets.zip";
                  document.body.appendChild(a);
                  a.click();

                  // Cleanup
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  console.error("Error downloading group assets:", error);
                }
              }}
            >
              Download All Assets (ZIP)
            </Button>
          </label>
        </Grid>
      </Grid>
    </Modal>
  )
}